import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Activity, 
  Search, 
  Check, 
  CheckCircle, 
  ArrowLeft, 
  User, 
  Heart,
  Clock,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RehabKioskPage() {
  const [step, setStep] = useState('search'); // 'search', 'exercises', 'complete'
  const [searchQuery, setSearchQuery] = useState('');
  const [players, setPlayers] = useState([]);
  const [filteredPlayers, setFilteredPlayers] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [playerPlans, setPlayerPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [painLevel, setPainLevel] = useState(5);
  const [notes, setNotes] = useState('');
  const [success, setSuccess] = useState(false);

  // Load players with active rehab plans
  useEffect(() => {
    loadPlayers();
  }, []);

  // Filter players as user types
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredPlayers([]);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = players.filter(p => 
        p.name.toLowerCase().includes(query) ||
        (p.locker_number && p.locker_number.toString().includes(query)) ||
        (p.jersey_number && p.jersey_number.toString().includes(query))
      );
      setFilteredPlayers(filtered);
    }
  }, [searchQuery, players]);

  const loadPlayers = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/kiosk/rehab/players`);
      setPlayers(res.data);
    } catch (err) {
      console.error('Failed to load players:', err);
    }
  };

  const selectPlayer = async (player) => {
    setLoading(true);
    try {
      // Check in the player
      await axios.post(`${BACKEND_URL}/api/kiosk/rehab/checkin`, {
        player_id: player.id
      });

      // Get their treatment plans
      const res = await axios.get(`${BACKEND_URL}/api/kiosk/rehab/player/${player.id}/plans`);
      setSelectedPlayer({ ...player, name: res.data.player_name });
      setPlayerPlans(res.data.plans);
      setStep('exercises');
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error('No active treatment plan found for this player');
      } else {
        toast.error('Failed to load rehab plan');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleExercise = async (planId, exerciseIndex, currentlyCompleted) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/kiosk/rehab/exercise/complete`, {
        player_id: selectedPlayer.id,
        treatment_plan_id: planId,
        exercise_index: exerciseIndex,
        completed: !currentlyCompleted
      });

      // Update local state
      setPlayerPlans(prev => prev.map(p => {
        if (p.plan.id === planId) {
          return {
            ...p,
            completed_exercises: res.data.completed_exercises
          };
        }
        return p;
      }));
    } catch (err) {
      toast.error('Failed to update exercise');
    }
  };

  const completeSession = async (planId) => {
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/kiosk/rehab/session/complete`, {
        player_id: selectedPlayer.id,
        treatment_plan_id: planId,
        pain_level: painLevel,
        notes: notes || null
      });

      setSuccess(true);
      toast.success('Rehab session completed!');

      // Reset after 3 seconds
      setTimeout(() => {
        setStep('search');
        setSearchQuery('');
        setSelectedPlayer(null);
        setPlayerPlans([]);
        setPainLevel(5);
        setNotes('');
        setSuccess(false);
      }, 3000);
    } catch (err) {
      toast.error('Failed to complete session');
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    setStep('search');
    setSearchQuery('');
    setSelectedPlayer(null);
    setPlayerPlans([]);
    setPainLevel(5);
    setNotes('');
  };

  // Success Screen
  if (success) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="text-center animate-pulse">
          <div className="w-32 h-32 bg-[#32D74B] rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-20 h-20 text-white" />
          </div>
          <h1 className="text-5xl font-bold text-white mb-4">GREAT WORK!</h1>
          <p className="text-2xl text-[#32D74B]">{selectedPlayer?.name}</p>
          <p className="text-xl text-[#71717A] mt-2">Rehab session complete</p>
        </div>
      </div>
    );
  }

  // Search Screen
  if (step === 'search') {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-2xl">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-[#FF3B30] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Activity className="w-12 h-12 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white tracking-tight">REHAB CHECK-IN</h1>
            <p className="text-[#71717A] mt-2">Search your name to start</p>
          </div>

          {/* Search Input */}
          <div className="relative mb-6">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-6 h-6 text-[#71717A]" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, locker #, or jersey #"
              className="w-full h-16 pl-14 pr-4 text-xl bg-[#1A1A1A] border-[#27272A] text-white placeholder:text-[#52525B]"
              autoFocus
              data-testid="rehab-search-input"
            />
          </div>

          {/* Player List */}
          {filteredPlayers.length > 0 && (
            <div className="bg-[#1A1A1A] border border-[#27272A] rounded-lg overflow-hidden max-h-96 overflow-y-auto">
              {filteredPlayers.map(player => (
                <button
                  key={player.id}
                  onClick={() => selectPlayer(player)}
                  disabled={loading}
                  className="w-full p-4 flex items-center gap-4 hover:bg-[#27272A] transition-colors text-left border-b border-[#27272A] last:border-b-0"
                  data-testid={`player-${player.id}`}
                >
                  <div className="w-12 h-12 bg-[#FF3B30] rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-white text-lg font-semibold">{player.name}</p>
                    <p className="text-[#71717A] text-sm">
                      {player.locker_number && `Locker #${player.locker_number}`}
                      {player.locker_number && player.jersey_number && ' • '}
                      {player.jersey_number && `Jersey #${player.jersey_number}`}
                    </p>
                  </div>
                  <Activity className="w-5 h-5 text-[#FF3B30]" />
                </button>
              ))}
            </div>
          )}

          {searchQuery && filteredPlayers.length === 0 && (
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 text-[#71717A] mx-auto mb-2" />
              <p className="text-[#71717A]">No players found with active rehab plans</p>
            </div>
          )}

          {/* Instructions */}
          {!searchQuery && (
            <div className="text-center mt-8">
              <p className="text-[#52525B]">Type your name to find your treatment plan</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Exercises Screen
  if (step === 'exercises') {
    return (
      <div className="min-h-screen bg-[#0A0A0A] p-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="ghost"
              onClick={goBack}
              className="text-[#71717A] hover:text-white"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-6 h-6" />
            </Button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-white">{selectedPlayer?.name}</h1>
              <p className="text-[#71717A]">Complete your exercises below</p>
            </div>
          </div>

          {/* Treatment Plans */}
          {playerPlans.map((planData, planIndex) => {
            const plan = planData.plan;
            const completedExercises = planData.completed_exercises || [];
            const allDone = plan.exercises.length > 0 && 
              plan.exercises.every((_, i) => completedExercises.includes(i));

            return (
              <Card key={plan.id} className="bg-[#121212] border-[#27272A] mb-6">
                <CardHeader className="border-b border-[#27272A]">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-white flex items-center gap-2">
                        <Heart className="w-5 h-5 text-[#FF3B30]" />
                        {plan.injury_type || 'Treatment Plan'}
                      </CardTitle>
                      <p className="text-sm text-[#71717A] mt-1">
                        {plan.instructions || 'Follow the exercises below'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-[#71717A]">
                        <Clock className="w-4 h-4 inline mr-1" />
                        ~{plan.estimated_duration_minutes || 30} min
                      </p>
                      <p className="text-sm text-[#32D74B]">
                        {completedExercises.length}/{plan.exercises.length} done
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-4 space-y-3">
                  {/* Exercises */}
                  {plan.exercises.map((exercise, exerciseIndex) => {
                    const isCompleted = completedExercises.includes(exerciseIndex);
                    return (
                      <button
                        key={exerciseIndex}
                        onClick={() => toggleExercise(plan.id, exerciseIndex, isCompleted)}
                        className={`w-full p-4 rounded-lg flex items-center gap-4 transition-all ${
                          isCompleted 
                            ? 'bg-[#32D74B]/20 border border-[#32D74B]' 
                            : 'bg-[#1A1A1A] border border-[#27272A] hover:border-[#3F3F46]'
                        }`}
                        data-testid={`exercise-${planIndex}-${exerciseIndex}`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          isCompleted ? 'bg-[#32D74B]' : 'bg-[#27272A]'
                        }`}>
                          {isCompleted ? (
                            <Check className="w-5 h-5 text-white" />
                          ) : (
                            <span className="text-white font-bold">{exerciseIndex + 1}</span>
                          )}
                        </div>
                        <div className="flex-1 text-left">
                          <p className={`font-semibold ${isCompleted ? 'text-[#32D74B]' : 'text-white'}`}>
                            {exercise.name}
                          </p>
                          <p className="text-sm text-[#71717A]">
                            {exercise.sets && `${exercise.sets} sets`}
                            {exercise.sets && exercise.reps && ' × '}
                            {exercise.reps && `${exercise.reps} reps`}
                            {exercise.duration_minutes && ` • ${exercise.duration_minutes} min`}
                          </p>
                          {exercise.notes && (
                            <p className="text-xs text-[#52525B] mt-1">{exercise.notes}</p>
                          )}
                        </div>
                      </button>
                    );
                  })}

                  {/* Complete Session */}
                  {allDone && (
                    <div className="mt-6 pt-4 border-t border-[#27272A]">
                      <p className="text-white font-semibold mb-3">How are you feeling?</p>
                      
                      {/* Pain Level */}
                      <div className="mb-4">
                        <p className="text-sm text-[#71717A] mb-2">Pain Level (1 = none, 10 = severe)</p>
                        <div className="flex gap-2">
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(level => (
                            <button
                              key={level}
                              onClick={() => setPainLevel(level)}
                              className={`w-10 h-10 rounded-lg font-bold transition-all ${
                                painLevel === level
                                  ? level <= 3 ? 'bg-[#32D74B] text-white' :
                                    level <= 6 ? 'bg-[#FFD60A] text-black' :
                                    'bg-[#FF3B30] text-white'
                                  : 'bg-[#27272A] text-[#71717A] hover:bg-[#3F3F46]'
                              }`}
                            >
                              {level}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Notes */}
                      <div className="mb-4">
                        <p className="text-sm text-[#71717A] mb-2">Notes (optional)</p>
                        <Input
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          placeholder="Any comments about today's session..."
                          className="bg-[#0A0A0A] border-[#27272A] text-white"
                        />
                      </div>

                      <Button
                        onClick={() => completeSession(plan.id)}
                        disabled={loading}
                        className="w-full h-14 bg-[#32D74B] hover:bg-[#32D74B]/90 text-white text-lg font-bold"
                        data-testid="complete-session-btn"
                      >
                        <CheckCircle className="w-6 h-6 mr-2" />
                        {loading ? 'Completing...' : 'COMPLETE SESSION'}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  return null;
}
